import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import type { Breadcrumb, Event, Span, Transaction } from '@sentry/vue';
import { inject } from 'vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import type { Client } from '@keyvue/one-api-client';

// The value of query parameters such as token, refresh_token and api_key, also when URL-encoded inside another
// parameter (a URL in ?redirect=). keyvue-app hands its refresh token over in /authenticate?token= and
// /document?token=; utils/handover-token.ts takes it out of the address bar at start-up, so this is the second line
// of defence.
const CREDENTIAL = /((?:[?&#]|%(?:25)*(?:3F|26|23))[\w.-]*(?:token|key|secret|password|signature)(?:=|%(?:25)*3D))(?:(?!%(?:25)*(?:26|23))[^&#\s])+/gi;

const scrub = (value: string) => value.replace(CREDENTIAL, '$1[Filtered]');

const hasCredential = (value: string) => scrub(value) !== value;

// Strings at any depth, as in extra.__serialized__ for a rejected plain object
const scrubDeep = (value: unknown, depth = 0): unknown => {
    if (typeof value === 'string') {
        return scrub(value);
    }
    if (depth >= 6 || value === null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => scrubDeep(item, depth + 1));
    }

    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, scrubDeep(item, depth + 1)]));
};

const scrubValues = (data: Record<string, unknown>) => scrubDeep(data) as Record<string, unknown>;

// vue-router's route params and query, which Sentry keeps in the trace context
const withoutRouteData = (contexts: Event['contexts']) => contexts && {
    ...contexts,
    trace: contexts.trace && { ...contexts.trace, data: undefined },
};

// For error events: the page URL, the referrer, the message, the exception texts and extra data
const withoutCredentials = <T extends Event>(event: T): T => {
    const { Referer, ...headers } = event.request?.headers ?? {};

    return {
        ...event,
        message: event.message && scrub(event.message),
        exception: event.exception && {
            ...event.exception,
            values: event.exception.values?.map((value) => ({ ...value, value: value.value && scrub(value.value) })),
        },
        extra: event.extra && scrubValues(event.extra),
        request: event.request && {
            ...event.request,
            url: event.request.url && scrub(event.request.url),
            headers: Referer ? { ...headers, Referer: scrub(Referer) } : headers,
        },
        contexts: withoutRouteData(event.contexts),
    };
};

// Resource spans list every script, style and image, signed URLs included
const isPageSpan = (span: Span) => !span.op?.startsWith('resource')
    && !hasCredential(`${span.description} ${JSON.stringify(span.data)}`);

// API request URLs carry search terms and list filters (a contact's e-mail address, a name): breadcrumbs keep the path
const withoutQuery = (value: unknown) => (typeof value === 'string' ? value.split(/[?#]/)[0] : value);

export default (app: App, router: Router) => {
    if (!import.meta.env.VITE_SENTRY_DSN) {
        return;
    }

    Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
        // Component props can hold contact and lead data, and Sentry is hosted in the US
        attachProps: false,
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),

                // No spans for API requests: their URLs carry list filters and search terms. This also stops the
                // sentry-trace and baggage headers to one-api and keyvue-api.
                traceXHR: false,
                traceFetch: false,
            }),
        ],

        release: APP_VERSION,

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,

        beforeSend: (event) => withoutCredentials(event),

        // Page usage only needs the route name: no page or referrer URL, no breadcrumbs, and no web-vitals tags that
        // name an element (lcp.url, lcp.element, cls.source.*: they can hold an image URL or a title/alt with a name)
        beforeSendTransaction: (event) => {
            const { breadcrumbs, request, tags, contexts, spans, ...transaction } = event;
            const pageTags = Object.fromEntries(Object.entries(tags ?? {})
                .filter(([key]) => !/^(lcp\.(url|element)|cls\.source)/.test(key)));
            const userAgent = request?.headers?.['User-Agent'];

            return {
                ...transaction,
                contexts: withoutRouteData(contexts),
                request: userAgent ? { headers: { 'User-Agent': userAgent } } : undefined,
                tags: pageTags,
                spans: spans?.filter(isPageSpan),
            };
        },

        beforeBreadcrumb: (breadcrumb: Breadcrumb) => {
            // Console breadcrumbs carry whatever the app logs, which can be contact and lead data; click and input
            // breadcrumbs describe the element, title and alt texts with names included
            if (breadcrumb.category === 'console' || breadcrumb.category?.startsWith('ui.')) {
                return null;
            }

            const data = breadcrumb.data && scrubValues(breadcrumb.data);

            return {
                ...breadcrumb,
                message: breadcrumb.message && scrub(breadcrumb.message),
                // xhr/fetch: url; navigation: from and to
                data: data && { ...data, url: withoutQuery(data.url), from: withoutQuery(data.from), to: withoutQuery(data.to) },
            };
        },
    });

    // Public pages (authenticate, logout) have no user to count. On the other pages the page view waits for the route
    // to resolve: beforeResolve loads the session first, and without request spans the idle transaction would
    // otherwise be sent after 1 s, before afterEach below has set the user and company.
    // The span of the navigation being resolved, with its `to` (vue-router passes the same object to beforeEach,
    // afterEach and onError). A new navigation supersedes it; a failed one only finishes its own.
    let pending: { to: object, span: Span, transaction: Transaction } | undefined;
    const finishPending = (to: object) => {
        if (pending?.to === to) {
            pending.span.finish();
            pending = undefined;
        }
    };

    router.beforeEach((to) => {
        const transaction = Sentry.getActiveTransaction();

        pending?.span.finish();
        pending = undefined;

        if (transaction && to.meta.anonymous === true) {
            transaction.sampled = false;
        } else if (transaction) {
            pending = {
                to,
                span: transaction.startChild({ op: 'navigation.resolve', description: 'route guards and session' }),
                transaction,
            };
        }
    });

    // A guard that throws (loadSession failing) skips afterEach. The page never rendered, so it isn't a page view;
    // the error itself is still reported.
    // Only the failing navigation's own transaction: an error from an older navigation leaves a newer page view alone.
    router.onError((error, to) => {
        if (pending?.to === to) {
            pending.transaction.sampled = false;
        }
        finishPending(to);
    });

    // Who is using the page, so usage can be counted per user and per company. Ids only, no e-mail or name. A
    // navigation that was aborted or cancelled leaves the user where they were, so it changes nothing.
    router.afterEach((to, from, failure) => {
        // Finishing only starts the 1 s idle timer, so the user and company below are still set before it's sent
        finishPending(to);

        if (failure) {
            return;
        }

        const session = to.meta.anonymous === true ? undefined : inject<Client>('client')?.session;
        const user = session?.currentUser;

        Sentry.configureScope((scope) => {
            scope.setUser(user ? { id: user.id.toString() } : null);
            scope.setTag('company', session?.currentCompany?.id.toString());
            scope.setTag('super_admin', user ? String(user.superAdmin) : undefined);
        });
    });
};
